import { Action, Selector, StateContext } from "@ngxs/store";
import { MedicalStateModel } from "./medical-model";
import { inject } from "@angular/core";
import { MedicalStateActions } from "./medical-actions";
import { append, iif, patch, removeItem, updateItem } from "@ngxs/store/operators";
import { AdjustedMedicalBill, RepricedMedicalBill } from "../../models/medical-bill";
import { MedicalService } from "../../services/medical.service";
import { Encounter } from "../../models/encounter";
import { Users } from "../user/user-actions";

export class MedicalState<E extends Encounter>
{

    constructor
    (
    )
    {

    }

    private _medical = inject(MedicalService);

    @Selector()
    static submissionProcessing<E extends Encounter>(state:MedicalStateModel<E>)
    {
        return (submissionId:number) => 
            {
                if(state.submissionsProcessing)
                    return state.submissionsProcessing.map(s => s.id).includes(submissionId);
                else
                    return false

            } 
    }

    @Selector()
    static processingMedicalBills<E extends Encounter>(state: MedicalStateModel<E>)
    {
        return state.submissionsProcessing;
    }

    @Selector()
    static submittedMedicalBills<E extends Encounter>(state: MedicalStateModel<E>) 
    {
      return state.submittedMedicalBills;
    }

    @Selector()
    static completeUnconfirmedSubmissions<E extends Encounter>(state: MedicalStateModel<E>)
    {
        return state.completeUnconfirmedSubmissions;
    }

    @Selector()
    static senders<E extends Encounter>(state:MedicalStateModel<E>)
    {
        return state.senders;
    }

    @Selector()
    static payers<E extends Encounter>(state:MedicalStateModel<E>)
    {
        return state.payers;
    }

    @Selector()
    static confirmedMedicalBills<E extends Encounter>(state: MedicalStateModel<E>)
    {
        return state.confirmedMedicalBills;
    }

    @Selector()
    static lockedRepricedBills<E extends Encounter>(state: MedicalStateModel<E>)
    {
        return state.lockedRepricedBills;
    }


    @Selector()
    static submissionState<E extends Encounter>(state:MedicalStateModel<E>)
    {
        return (submissionId:number) => 
            {
                if(state.submissionStates && submissionId in state.submissionStates)
                    return state.submissionStates[submissionId]
                else
                    return undefined;
            }
    }

    @Selector()
    static activeAdjustment<E extends Encounter>(state:MedicalStateModel<E>)
    {
        return (billId:number) => 
            {
                if(state.activeAdjustments && billId in state.activeAdjustments)
                    return state.activeAdjustments[billId]
                else
                    return undefined;
            }
    }

    @Selector()
    static activeRepricing<E extends Encounter>(state:MedicalStateModel<E>)
    {
        return (billId:number) => 
            {
                if(state.activeRepricings && billId in state.activeRepricings)
                    return state.activeRepricings[billId]
                else
                    return undefined;
            }
    }

    @Action([MedicalStateActions.ProcessorLoaded])
    processorLoaded(ctx: StateContext<MedicalStateModel<E>>) 
    {
        return ctx.dispatch(
            [
                new MedicalStateActions.RefreshBillSubmissions(),
                new MedicalStateActions.RefreshUnconfirmedBills(),
                new MedicalStateActions.RefreshConfirmedBills(),
                new MedicalStateActions.RefreshLockedBills(),
                new MedicalStateActions.RefreshBillsAwaitingPayment(),
                new MedicalStateActions.RefreshPayers(),
                new MedicalStateActions.RefreshSenders()
            ]
        );
    }

    @Action([MedicalStateActions.RefreshSenders])
    async refreshSenders(ctx:StateContext<MedicalStateModel<E>>, action: MedicalStateActions.RefreshSenders)
    {
        var senders = await this._medical.getSenders();

        ctx.setState
        (
            patch<MedicalStateModel<E>>({senders:senders})
        )
    }

    @Action([MedicalStateActions.RefreshPayers])
    async refreshPayers(ctx:StateContext<MedicalStateModel<E>>, action: MedicalStateActions.RefreshPayers)
    {
        var payers = await this._medical.getPayers();

        ctx.setState
        (
            patch<MedicalStateModel<E>>({payers:payers})
        )
    }

    @Action([MedicalStateActions.RefreshConfirmedBills])
    async refreshConfirmedBills(ctx: StateContext<MedicalStateModel<E>>, action: MedicalStateActions.RefreshBillSubmissions)
    {
        var confirmed = await this._medical.getConfirmedMedicalBills<E>();


        //Load up active adjustments and repricings
        var activeAdjustments = confirmed.reduce((a,c) => {return {...a, [c.id]:c.adjustments.find(adj => !adj.voided)}} , {} as Record<number, AdjustedMedicalBill<E> | undefined>)
        var activeRepricings = confirmed.reduce((a,c) => {return {...a, [c.id]:activeAdjustments[c.id]?.repricings.find(rep => !rep.voided)}} , {} as Record<number, RepricedMedicalBill<E> | undefined>)

        ctx.setState
        (
            patch<MedicalStateModel<E>>(
            {
                confirmedMedicalBills: confirmed,
                activeAdjustments: activeAdjustments,
                activeRepricings: activeRepricings
            })
        )

    }

    @Action([MedicalStateActions.RefreshBillSubmissions])
    async refreshBillSubmissions(ctx: StateContext<MedicalStateModel<E>>, action: MedicalStateActions.RefreshBillSubmissions)
    {
        var submissions = await this._medical.getUnprocessedSubmissions();
        ctx.setState
        (
            patch<MedicalStateModel<E>>(
            {
                submittedMedicalBills:submissions
            })
        )
    }

    @Action([MedicalStateActions.RefreshUnconfirmedBills])
    async refreshUnConfirmedBills(ctx: StateContext<MedicalStateModel<E>>, action: MedicalStateActions.RefreshUnconfirmedBills)
    {
        var draftBills = await this._medical.getUnconfirmedDraftMedicalBills();
        ctx.setState
        (
            patch<MedicalStateModel<E>>(
            {
                completeUnconfirmedSubmissions:draftBills
            })
        )
    }

    @Action([MedicalStateActions.RefreshBillsAwaitingPayment])
    async refreshRepricedBillsAwaitingPayment(ctx: StateContext<MedicalStateModel<E>>, action: MedicalStateActions.RefreshBillsAwaitingPayment)
    {
        var bills = await this._medical.getRepricedBillsAwaitingPayment();
        ctx.setState
        (
            patch<MedicalStateModel<E>>
            (
                {
                    repricedBillsAwaitingPayment:bills
                }
            )
        )
    }

    @Action([MedicalStateActions.RefreshLockedBills])
    async refreshLockedBills(ctx: StateContext<MedicalStateModel<E>>, action: MedicalStateActions.RefreshLockedBills)
    {
        var bills = await this._medical.getLockedRepricedBills();
        ctx.setState
        (
            patch<MedicalStateModel<E>>
            (
                {
                    lockedRepricedBills:bills
                }
            )
        )
    }

    @Action([MedicalStateActions.MedicalBillProcessorMedicalBillSubmitted])
    async submitMedicalBill(ctx: StateContext<MedicalStateModel<E>>, action: MedicalStateActions.MedicalBillProcessorMedicalBillSubmitted)
    {

        const state = ctx.getState();

        // //Fetch the thumbnail for this sucker
        // const fileUrl = await this._util.getFileImageURL(action.payload.submission.billThumbnail);

        //Update the list of submitted medical bills
        ctx.setState
        ( 
            patch<MedicalStateModel<E>>
            (
                { 
                    submittedMedicalBills: iif(!state.submittedMedicalBills.map(b => b.id).includes(action.payload.submission.id), append([action.payload.submission])),
                    // submissionThumbnails: {... state.submissionThumbnails, [action.payload.submission.id]: fileUrl}                                 
                }
            )
        );

    }

    @Action([MedicalStateActions.MedicalBillProcessorMedicalBillProcessingComplete])
    medicalBillProcessingComplete(ctx: StateContext<MedicalStateModel<E>>, action: MedicalStateActions.MedicalBillProcessorMedicalBillProcessingComplete)
    {
        var existingState = ctx.getState();
        if(existingState.submittedMedicalBills.map(a => a.id).includes(action.payload.submission.id))
        {
            //Update the list of submitted medical bills
            // var index = existingState.submittedMedicalBills.findIndex(a => a.id == action.payload.submission.id)

            const state = ctx.getState();
            const { [action.payload.submission.id]: removedItem, ...newSubmissionStates } = state.submissionStates;

            ctx.setState
            ( 
                patch<MedicalStateModel<E>>
                (
                    { 
                        submittedMedicalBills: removeItem(b => b.id == action.payload.submission.id),
                        submissionStates: newSubmissionStates,
                        completeUnconfirmedSubmissions: iif(!state.completeUnconfirmedSubmissions.map(b => b.id).includes(action.payload.submission.id),append([action.payload.draftBill])),
                    }
                )
            );

            if(existingState.submissionsProcessing && existingState.submissionsProcessing.map(a => a.id).includes(action.payload.submission.id))
            {
                var processingIndex = existingState.submissionsProcessing.findIndex(a => a.id == action.payload.submission.id)
                
                ctx.setState
                (
                    patch<MedicalStateModel<E>>({submissionsProcessing: removeItem(processingIndex)})
                )
            }

        }
    }

    @Action([MedicalStateActions.StartProcessingMedicalBillSubmission])
    startProcessingMedicalBill(ctx: StateContext<MedicalStateModel<E>>, action:MedicalStateActions.StartProcessingMedicalBillSubmission)
    {
        const state = ctx.getState();
        ctx.setState
        (
            patch<MedicalStateModel<E>>
            (
                {
                    submissionsProcessing: iif(!state.submissionsProcessing.map(b => b.id).includes(action.payload.submission.id),append([action.payload.submission]))
                }
            )
        )
    }

    @Action([MedicalStateActions.MedicalBillServiceMedicalBillConfirmed<E>])
    confirmMedicalBill(ctx:StateContext<MedicalStateModel<E>>, action:MedicalStateActions.MedicalBillServiceMedicalBillConfirmed<E>)
    {
        const state = ctx.getState();
        //Remove the draft medical bill that was just confirmed
        //Add a confirmed medical bill to the list
        
        ctx.setState
        (
            patch<MedicalStateModel<E>>
            (
                {
                    completeUnconfirmedSubmissions:removeItem(i => i.id == action.payload.draft.id),
                    confirmedMedicalBills:iif(!state.confirmedMedicalBills.map(b => b.id).includes(action.payload.medicalBill.id),append([action.payload.medicalBill]))
                }
            )
        )
    }

    @Action([MedicalStateActions.RepricedBillLocked<E>])
    repricedBillLocked(ctx:StateContext<MedicalStateModel<E>>, action:MedicalStateActions.RepricedBillLocked<E>)
    {
        const existingState = ctx.getState();

        //remove medical bill from list of confirmed bills and put the repriced bill over in the list of locked bills awaiting send
        ctx.setState
        (
            patch<MedicalStateModel<E>>
            (
                {
                    confirmedMedicalBills: iif(existingState.confirmedMedicalBills.map(b => b.id).includes(action.payload.repricedBill.originalBillId),removeItem(i => i.id == action.payload.repricedBill.originalBillId)),
                    lockedRepricedBills: iif(!existingState.lockedRepricedBills.map(b => b.id).includes(action.payload.repricedBill.id),append([action.payload.repricedBill])),
                    activeAdjustments:iif(action.payload.repricedBill.originalBillId in existingState.activeAdjustments, {... existingState.activeAdjustments, [action.payload.repricedBill.originalBillId]: undefined}),
                    activeRepricings:iif(action.payload.repricedBill.originalBillId in existingState.activeRepricings, {... existingState.activeRepricings, [action.payload.repricedBill.originalBillId]: undefined})

                }
            )
        )
    }

    @Action([MedicalStateActions.BillSentToClearingHouse<E>])
    billSentToClearingHouse(ctx:StateContext<MedicalStateModel<E>>, action:MedicalStateActions.BillSentToClearingHouse<E>)
    {

        const existingState = ctx.getState();

        ctx.setState
        (
            patch<MedicalStateModel<E>>
            (
                {
                    lockedRepricedBills: iif(existingState.lockedRepricedBills.map(b => b.id).includes(action.payload.repricedBill.id),removeItem(b => b.id == action.payload.repricedBill.id))
                }
            )
        )
    }


    @Action([MedicalStateActions.MedicalBillAdjusted<E>, MedicalStateActions.AdjustedBillVoided<E>])
    billAdjustedOrVoided(ctx:StateContext<MedicalStateModel<E>>, action:MedicalStateActions.MedicalBillAdjusted<E> | MedicalStateActions.AdjustedBillVoided<E> )
    {
        const existingState = ctx.getState();


        //First, find the bill that this sucker is attached to
        const originalBillId = action.payload.adjustedBill.originalBillId;
        // const activeAdjustment = action instanceof MedicalStateActions.MedicalBillAdjusted ? action.payload.adjustedBill : undefined

        // ctx.setState
        // (
        //     patch<MedicalStateModel<E>>
        //     (
        //         {
        //             confirmedMedicalBills:updateItem(b => b.id == originalBillId,
        //                 patch
        //                 (
        //                     {
        //                         adjustments: iif(action instanceof MedicalStateActions.MedicalBillAdjusted , append([action.payload.adjustedBill]), updateItem(r => r.id == action.payload.adjustedBill.id, action.payload.adjustedBill))
        //                     }
        //                 )),
        //             activeAdjustments: {... existingState.activeAdjustments, [originalBillId]: activeAdjustment}
        //         }
        //     )
        // )

        //Choosing this method doesn't replace the existing bill with a plain copy... But it does update the underlying adjustments
        var existingBill = existingState.confirmedMedicalBills.find(b => b.id == originalBillId);
        if(existingBill)
        {
            if(action instanceof MedicalStateActions.MedicalBillAdjusted)
            {
                existingBill.adjustments = [ ... existingBill.adjustments, action.payload.adjustedBill];
            }
            else
            {
                existingBill.adjustments = [ ... existingBill.adjustments.filter(a => a.id != action.payload.adjustedBill.id)]
            }
            
            //Is there an active adjustment any longer?
            const activeAdjustment = existingBill.adjustments.find(adj => !adj.voided);

            ctx.setState
            (
                patch<MedicalStateModel<E>>
                (
                    {
                        confirmedMedicalBills:updateItem(b => b.id == originalBillId, existingBill),
                        activeAdjustments: {... existingState.activeAdjustments, [existingBill.id]: activeAdjustment}
                    }
                )
            )
        }

        // var existingBillAdjustments = existingState.confirmedMedicalBills.find(b => b.id == originalBillId)?.adjustments;
        // if(existingBillAdjustments)
        // {
        //     if(action instanceof MedicalStateActions.MedicalBillAdjusted)
        //     {
        //         existingBillAdjustments = [ ... existingBillAdjustments, action.payload.adjustedBill];
        //     }
        //     else
        //     {
        //         existingBillAdjustments = [ ... existingBillAdjustments.filter(a => a.id != action.payload.adjustedBill.id)]
        //     }

        //     ctx.setState
        //     (
        //         patch<MedicalStateModel<E>>
        //         (
        //             {
        //                 confirmedMedicalBills:updateItem(b => b.id == originalBillId, plainToInstance(MedicalBill<E>, 
        //                 patch
        //                 (
        //                     {
        //                         adjustments: instanceToInstance(existingBillAdjustments)
        //                     }
        //                 )))
        //             }
        //         )
        //     )
        // }
    }

    @Action([MedicalStateActions.AdjustedBillRepriced<E>, MedicalStateActions.RepricedBillVoided<E>])
    adjustmentRepricedOrVoided(ctx:StateContext<MedicalStateModel<E>>, action:MedicalStateActions.AdjustedBillRepriced<E> | MedicalStateActions.RepricedBillVoided<E> )
    {
        const existingState = ctx.getState();

        //First, find the bill that this sucker is attached to
        const originalBillId = action.payload.repricedBill.originalBillId;
        // ctx.setState
        // (
        //     patch<MedicalStateModel<E>>
        //     (
        //         {
        //             confirmedMedicalBills:updateItem(b => b.id == originalBillId,
        //                 patch
        //                 (
        //                     {
        //                         adjustments: updateItem(a => a.id == action.payload.repricedBill.adjustmentId,
        //                             patch
        //                             (
        //                                 {
        //                                     repricings: iif(action instanceof MedicalStateActions.AdjustedBillRepriced , append([action.payload.repricedBill]), updateItem(r => r.id == action.payload.repricedBill.id, action.payload.repricedBill))
        //                                 }
        //                             )
        //                         )

        //                     }
        //                 ))
        //         }
        //     )
        // )
        var existingBill = existingState.confirmedMedicalBills.find(b => b.id == originalBillId);
        
        if(existingBill)
        {
            var adjustment =  existingBill.adjustments.find(a => a.id == action.payload.repricedBill.adjustmentId);
            if(adjustment)
            {
                if(action instanceof MedicalStateActions.AdjustedBillRepriced)
                {

                    adjustment.repricings = [ ... adjustment.repricings, action.payload.repricedBill];
                }
                else
                {
                    adjustment.repricings = [ ... adjustment.repricings.filter(r => r.id != action.payload.repricedBill.id)]
                }
                
                //Is there an active repricing any longer?
                const activeRepricing = adjustment.repricings.find(rep => !rep.voided);
                ctx.setState
                (
                    patch<MedicalStateModel<E>>
                    (
                        {
                            confirmedMedicalBills:updateItem(b => b.id == originalBillId, existingBill),
                            activeRepricings: {... existingState.activeRepricings, [existingBill.id]: activeRepricing}
                        }
                    )
                )
            }
        }
    }

    @Action([MedicalStateActions.MedicalBillDeleted<E>])
    deleteMedicalBill(ctx: StateContext<MedicalStateModel<E>>, action: MedicalStateActions.MedicalBillDeleted<E>)
    {
        const existingState = ctx.getState();
        ctx.setState
        (
            patch<MedicalStateModel<E>>
            (
                {
                    activeAdjustments: iif(action.payload.bill.id in existingState.activeAdjustments, {... existingState.activeAdjustments, [action.payload.bill.id]: undefined}, existingState.activeAdjustments),
                    activeRepricings: iif(action.payload.bill.id in existingState.activeRepricings, {... existingState.activeRepricings, [action.payload.bill.id]: undefined}, existingState.activeRepricings),
                    confirmedMedicalBills: iif(existingState.confirmedMedicalBills.map(b => b.id).includes(action.payload.bill.id), existingState.confirmedMedicalBills.filter(b => b.id != action.payload.bill.id), removeItem(b => b.id == action.payload.bill.id))
                }
            )
        )
    }

    @Action([MedicalStateActions.DraftMedicalBillDeleted])
    deleteDraftMedicalBill(ctx: StateContext<MedicalStateModel<E>>, action: MedicalStateActions.DraftMedicalBillDeleted)
    {
        const existingState = ctx.getState();
        ctx.setState
        (
            patch<MedicalStateModel<E>>
            (
                {
                   completeUnconfirmedSubmissions: iif(existingState.completeUnconfirmedSubmissions.map(b => b.id).includes(action.payload.bill.id), existingState.completeUnconfirmedSubmissions.filter(b => b.id != action.payload.bill.id), removeItem(b => b.id == action.payload.bill.id))
                }
            )
        )
    }

    @Action([MedicalStateActions.MedicalBillProcessorMedicalBillBeingAnalyzed])
    analyzeMedicalBill(ctx: StateContext<MedicalStateModel<E>>, action: MedicalStateActions.MedicalBillProcessorMedicalBillBeingAnalyzed)
    {
        this.updateSubmissionState(ctx,action.payload.submission.id,"Analyzing Submission");
    }

    @Action([MedicalStateActions.MedicalBillProcessorIdentifyingPatient])
    identifyPatient(ctx: StateContext<MedicalStateModel<E>>, action: MedicalStateActions.MedicalBillProcessorIdentifyingPatient)
    {
        this.updateSubmissionState(ctx,action.payload.submission.id,"Identifying Patient");
    }

    @Action([MedicalStateActions.MedicalBillProcessorIdentifyingProviders])
    identifyProvider(ctx: StateContext<MedicalStateModel<E>>, action: MedicalStateActions.MedicalBillProcessorIdentifyingProviders)
    {
        this.updateSubmissionState(ctx,action.payload.submission.id,"Identifying Providers");
    }

    @Action([MedicalStateActions.MedicalBillProcessorProcessingLines])
    processingLines(ctx: StateContext<MedicalStateModel<E>>, action: MedicalStateActions.MedicalBillProcessorProcessingLines)
    {
        this.updateSubmissionState(ctx,action.payload.submission.id,"Processing Lines");
    }




    updateSubmissionState(ctx:StateContext<MedicalStateModel<E>>, id: number, status: string)
    {
        const state = ctx.getState();

        //Update the list of submitted medical bills
        ctx.setState
        ( 
            patch<MedicalStateModel<E>>
            (
                { submissionStates: {... state.submissionStates, [id]: status}}
            )
        );        
    }

}